import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import React, { useCallback } from 'react';
import { trackGA4SelectShippingPreference } from '../../pages/cart/utils/analytics';
import { ShippingMethod } from '../../types/cart/ShippingMethod';
import { ShippingOption } from './ShippingOption';
import styles from './ShippingOptions.module.scss';
import {
  type DeliveryDetail,
  DeliveryTimeFrameType,
  type ShippingOptionEstimate,
} from './types';

export const getShippingAmount = (
  showTaxSeparately: boolean,
  standardIncTaxTotal?: number,
  expressIncTaxTotal?: number,
  standardExTaxTotal?: number,
  expressExTaxTotal?: number,
  selectedShipping?: ShippingMethod
) => {
  switch (selectedShipping) {
    case ShippingMethod.Standard:
      return showTaxSeparately
        ? standardExTaxTotal || 0
        : standardIncTaxTotal || 0;
    case ShippingMethod.Express:
      return showTaxSeparately
        ? expressExTaxTotal || 0
        : expressIncTaxTotal || 0;
    default:
      return 0;
  }
};

export interface ShippingOptionsProps {
  shippingMethod: ShippingMethod | undefined;
  shippingEstimates?: {
    standard?: ShippingOptionEstimate;
    express?: ShippingOptionEstimate;
  };
  currency: string;
  deliveryDates?: {
    standard?: DeliveryDetail;
    express?: DeliveryDetail;
  };
  showTaxSeparately?: boolean;
  flatRateApplied?: boolean;
  isLoading?: boolean;
  onChange?: (shippingMethod: ShippingMethod) => Promise<unknown>;
}

export const ShippingOptions = ({
  shippingMethod,
  shippingEstimates,
  currency,
  deliveryDates,
  showTaxSeparately,
  flatRateApplied,
  isLoading,
  onChange,
}: ShippingOptionsProps) => {
  const onShippingPreferenceUpdate = useCallback(
    async (selected: ShippingMethod) => {
      await onChange?.(selected);
      trackGA4SelectShippingPreference({ shippingPreference: selected });
    },
    [onChange]
  );

  const standard = shippingEstimates?.standard;
  const express = shippingEstimates?.express;

  const standardDeliveryDetail = deliveryDates?.standard;
  const expressDeliveryDetail = deliveryDates?.express;

  const hasStandard = !!(standard && standardDeliveryDetail);
  const hasExpress = !!(express && expressDeliveryDetail);

  // Display Express above Standard when Standard is DD and Express is LOBD
  // See https://redbubble.atlassian.net/wiki/spaces/BE/pages/2559770632/Delivery+dates+and+Last+order+by+dates+on+web
  const showLOBDFirst =
    standardDeliveryDetail?.type === DeliveryTimeFrameType.DeliveryTimeFrame &&
    expressDeliveryDetail?.type === DeliveryTimeFrameType.LastOrderByDate;

  const standardAmount = showTaxSeparately
    ? standard?.amountExcludingTax || 0
    : standard?.amountWithTax || 0;
  const expressAmount = showTaxSeparately
    ? express?.amountExcludingTax || 0
    : express?.amountWithTax || 0;

  const currentShippingAmount = getShippingAmount(
    showTaxSeparately || false,
    standard?.amountWithTax,
    express?.amountWithTax,
    standard?.amountExcludingTax,
    express?.amountExcludingTax,
    shippingMethod
  );

  const standardDelta = standardAmount - currentShippingAmount;
  const expressDelta = expressAmount - currentShippingAmount;

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" marginBottom="xs">
        <div
          data-testid="shipping-options-loading"
          className={styles.shippingOptionsLoading}
        >
          <Box direction={constants.HORIZONTAL}>
            <Skeleton height={80} animated />
          </Box>
          {
            // Show a skeleton for express if:
            //  1. Express is already in the data i.e. reloading, OR
            //  2. We don't have any data for standard even though this should always be available i.e. initial load
            // The reason we do point 2 is we're assuming express will eventually become available once the shipping
            // data finally loads (express is offered far more often than not)
            (hasExpress || !hasStandard) && (
              <Box direction={constants.HORIZONTAL}>
                <Skeleton height={80} animated />
              </Box>
            )
          }
        </div>
      </Box>
    );
  }

  return (
    <Box
      data-testid="shipping"
      display="flex"
      flexDirection={showLOBDFirst ? 'column-reverse' : 'column'}
      paddingBottom="xs"
    >
      {hasStandard && (
        <ShippingOption
          flatRateApplied={flatRateApplied}
          currency={currency}
          priceDelta={standardDelta}
          shippingMethod={ShippingMethod.Standard}
          deliveryDetail={standardDeliveryDetail}
          isSelected={shippingMethod === ShippingMethod.Standard}
          onChange={onShippingPreferenceUpdate}
        />
      )}

      {hasExpress && (
        <ShippingOption
          flatRateApplied={flatRateApplied}
          currency={currency}
          priceDelta={expressDelta}
          shippingMethod={ShippingMethod.Express}
          deliveryDetail={expressDeliveryDetail}
          isSelected={shippingMethod === ShippingMethod.Express}
          onChange={onShippingPreferenceUpdate}
        />
      )}
    </Box>
  );
};
